<template>
  <v-navigation-drawer
    width="220"
    clipped
    app
    fixed
    :value="value"
    @input="$emit('input', $event)"
  >
    <v-list
      dense
    >
      <v-list-item
        v-for="(item, i) in filterMenuItems(top)"
        :key="i"
        :to="item.route"
        link
      >
        <v-list-item-icon>
          <v-icon>{{ item.icon }}</v-icon>
        </v-list-item-icon>
        <v-list-item-content class="py-0">
          <v-list-item-title>
            {{ item.title }}
          </v-list-item-title>
          <v-list-item-subtitle
            v-if="item.subtitle"
            v-text="item.subtitle"
          />
        </v-list-item-content>
        <v-icon
          v-if="item.addedDate && !weekHasPassed(item.addedDate)"
          color="accent"
        >
          mdi-new-box
        </v-icon>
      </v-list-item>
      <v-divider />
      <template
        v-for="(item, index) in filterMenuItems(menuItems)"
      >
        <!--Normal Menu Handling-->
        <v-divider
          v-if="item.divider"
          :key="index + '-divider'"
          color="white"
        />
        <v-subheader
          v-if="item.subheader"
          :key="item.subheader"
        >
          {{ item.subheader }}
        </v-subheader>
        <v-list-item
          v-if="!item.subheader && !item.divider"
          :key="item.title"
          :to="item.route"
          link
        >
          <v-list-item-content>
            <v-list-item-title>{{ item.title }} {{ item.icon }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action
            v-if="item.canCreate"
            class="my-0"
          >
            <router-link
              :to="item.routeCreate"
              class="caption"
            >
              <v-btn
                x-small
                icon
              >
                <v-icon x-small>
                  mdi-plus-thick
                </v-icon>
              </v-btn>
            </router-link>
          </v-list-item-action>
          <v-list-item-icon
            v-if="item.addedDate && !weekHasPassed(item.addedDate)"
          >
            <v-icon color="accent">
              mdi-new-box
            </v-icon>
          </v-list-item-icon>
        </v-list-item>
      </template>
    </v-list>
    <template v-slot:append>
      <v-list
        class="pa-0"
        dense
      >
        <v-list-item
          :href="patchNotesUrl"
          target="_blank"
        >
          <v-list-item-content>
            <v-list-item-title>
              <v-fade-transition>
                <span>{{ hostName }} v{{ getAppVersion }}&nbsp;</span>
                <v-icon
                  small
                  color="accent lighten-1"
                >
                  mdi-alert-octagram
                </v-icon>
                <v-divider />
                <small
                  class="accent--text"
                >
                  please read the new patch notes
                </small>
              </v-fade-transition>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from 'vuex'
import utils from '@/helpers/utils'
import config from '@/../izzi_config'

export default {
  name: 'AppNavigation',
  props: {
    value: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    hostName: config.name
  }),
  computed: {
    ...mapGetters({
      can: 'can',
      user: 'currentUser'
    }),
    ...mapGetters(['getAppVersion']),
    patchNotesUrl () {
      return `${config.frontend}/site/patchnotes/`
    },
    menuItems () {
      return [
        {
          title: 'Check-Ins',
          route: { name: 'checkin-list' },
          can: this.can('api.view_checkin')
        },
        {
          title: 'Purchases',
          route: { name: 'purchase-list' },
          can: this.can('api.view_purchase'),
          routeCreate: { name: 'create-purchase' },
          canCreate: this.can('api.add_purchase')
        },
        {
          title: 'Intakes',
          route: { name: 'intake-list' },
          can: this.can('api.view_intake') || this.can('api.add_intake'),
          routeCreate: { name: 'create-intake' },
          canCreate: this.can('api.add_intake')
        },
        {
          divider: true,
          can: this.can('api.view_purchase') || this.can('api.view_checkin')
        },
        {
          title: 'Lots',
          route: { name: 'lot-list' },
          can: this.can('api.view_lot'),
          routeCreate: { name: 'create-lot' },
          canCreate: this.can('api.add_lot')
        },
        {
          title: 'Shipment Summaries',
          route: { name: 'shipment-summary-list' },
          can: this.can('api.view_shipmentsummary'),
          routeCreate: { name: 'create-shipment-summary' },
          canCreate: this.can('api.add_shipmentsummary')
        },
        {
          title: 'Settlements',
          route: { name: 'lot-settlement-list' },
          can: this.can('api.view_brokeredsettlement'),
          addedDate: '2024/7/19'
        },
        {
          title: 'Advances',
          route: { name: 'advance-list' },
          can: this.can('api.view_advance'),
          addedDate: '2024/8/2'
        },
        {
          title: 'PPM Calculators',
          route: { name: 'ppm-calculation-list' },
          can: this.can('api.view_ppmcalculation'),
          routeCreate: { name: 'create-ppm-calculation' },
          canCreate: this.can('api.add_ppmcalculation')
        },
        {
          title: 'Smelters',
          route: { name: 'smelter-list' },
          can: this.can('api.view_smelter')
        },
        {
          title: 'Hedges',
          route: { name: 'hedge-list' },
          can: this.can('api.view_hedge'),
          routeCreate: { name: 'create-hedge' },
          canCreate: this.can('api.add_hedge')
        },
        {
          title: 'Yards',
          route: { name: 'yard-list' },
          can: this.can('api.view_yard'),
          routeCreate: { name: 'create-yard' },
          canCreate: this.can('api.add_yard')
        },
        {
          subheader: 'Catalog/Inventory',
          can: this.can('api.view_catalog') || this.can('api.view_inventory')
        },
        {
          title: 'Inventory/Cut-Plans',
          route: { name: 'inventory-list' },
          can: this.can('api.view_inventory')
        },
        {
          subheader: 'Transit',
          can: this.can('logistics.view_transit')
        },
        {
          title: 'Transit',
          route: { name: 'transit-list' },
          can: this.can('logistics.view_transit')
        },
        {
          title: 'Inventory Manifests',
          route: { name: 'manifest-list' },
          can: this.can('api.view_inventorymanifest')
        },
        {
          title: 'Lot Manifests',
          route: { name: 'lot-manifest-list' },
          can: this.can('api.view_lotmanifest')
        },
        {
          title: 'Brokers',
          route: { name: 'broker-list' },
          can: this.can('logistics.view_broker')
        },
        {
          title: 'Carriers',
          route: { name: 'carrier-list' },
          can: this.can('logistics.view_carrier')
        }
      ]
    },
    top () {
      return [
        { title: 'Dashboard', icon: 'mdi-view-dashboard-outline', route: { name: 'dashboard' }, can: true },
        {
          title: 'Allocations',
          icon: 'mdi-format-columns',
          route: { name: 'current-allocations' },
          can: this.can('api.view_lot_allocation')
        },
        {
          title: 'Calendar',
          icon: 'mdi-calendar',
          route: { name: 'calendar' },
          can: this.can('frontend.view_calendar')
        },
        {
          title: 'Cut Plan',
          icon: 'mdi-order-numeric-descending',
          route: { name: 'cut-plan-list' },
          can: this.can('api.view_cutplan')
        },
        {
          title: 'Current Purchases',
          icon: 'mdi-table',
          route: { name: 'current-purchases' },
          can: this.can('api.view_current_purchase')
        },
        {
          title: 'Reporting',
          icon: 'mdi-clipboard-text-outline',
          route: { name: 'reports' },
          can: this.can('frontend.view_reports')
        },
        {
          title: 'Transit',
          icon: 'mdi-truck-delivery-outline',
          route: { name: 'transit-dashboard' },
          can: this.can('logistics.view_transit')
        },
        {
          title: 'Inventory On Hand',
          subtitle: this.$store.getters.getLastInventoryOnHandLocation,
          icon: 'mdi-transfer',
          route: {
            name: 'inventory-on-hand',
            query: {
              location_name: this.$store.getters.getLastInventoryOnHandLocation
            }
          },
          can: this.can('api.view_inventory_on_hand'),
          addedDate: '2021/6/2'
        }
      ]
    }
  },
  methods: {
    weekHasPassed (date) {
      return utils.timeHasPassed(date, 1, 'w')
    },
    /**
     * Determine what links the user can view
     * @param {Array} menuItems
     */
    filterMenuItems (menuItems) {
      return menuItems.filter(i => i.can)
    }
  },
  mounted () {
    this.$emit('input', this.$vuetify.breakpoint.mdAndUp)
  }
}
</script>

<style scoped>
a {
  text-decoration: none;
}

.v-list-item--dense, .v-list--dense .v-list-item {
  min-height: 36px;
}
</style>
